import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  forwardRef,
  inject,
  input,
  model,
  OnDestroy,
  OnInit,
  output,
  signal,
  TemplateRef,
  untracked,
} from '@angular/core';
import { ConnectedPosition } from '@angular/cdk/overlay';
import {
  AbstractControl,
  ControlContainer,
  FormArray,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  AssignList,
  LookupFilter,
  LookupOptionItem,
  OnSelectData,
} from './lookup-field-item';
import { LookupFieldService } from './lookup-field.service';
import { LoaderComponent } from '../loader/loader.component';
import { KeyValuePipe } from '../../pipes/key-value/key-value.pipe';
import {
  debounceTime,
  delay,
  EMPTY,
  finalize,
  forkJoin,
  of,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
} from 'rxjs';
import { ApiResponse } from '../../../core/common/models/api-response.model';
import { AvatarComponent } from '../avatar/avatar.component';
import { TrimTooltipDirective } from '../../directives/trim-tooltip/trim-tooltip.directive';
import { filterInputTypesEnum } from '../../enums/filter-input-type';
import { SearchMinLength } from '../../../core/common/enums/search-min-length';
import { DatePipe, NgTemplateOutlet, SlicePipe } from '@angular/common';
import { FormBuilderService } from '../../services/form-builder/form-builder.service';
import {
  AddIconComponent,
  ArrowLeftIconComponent,
  ChevronDownIconComponent,
  CloseIconComponent,
  InfoIconComponent,
  RefreshIconComponent,
} from '@kuiper/icons';
import { BadgeComponent } from '../badge/badge.component';
import { StatusPipe } from '../../pipes/status/status.pipe';
import { FeatureFlagsService } from '../../../core/common/services/feature-flags/feature-flags.service';
import { FEATURE_FLAGS } from '../../../core/common/constants/feature-flags.constants';
import { TranslocoPipe } from '@jsverse/transloco';
import { TriggerAction } from '../../../form-builder/interface/request-form-builder.model';
import { TriggerActionHandler } from './trigger-action-handler';
import { Router } from '@angular/router';
import { TenantService } from '../../../core/common/services/tenant/tenant.service';
import { EMPLOYEE_ROUTES } from '../../../core/common/constants/employee.routing.constants';
import { LookupResource } from './lookup-resource.model';
import { LookupResourceLinkService } from './lookup-resource-link.service';
import { DropdownOverlayDirective } from '../../../core/common/directives/dropdown-overlay/dropdown-overlay.directive';

@Component({
  selector: 'lib-lookup-field',
  imports: [
    ReactiveFormsModule,
    LoaderComponent,
    KeyValuePipe,
    AvatarComponent,
    TrimTooltipDirective,
    SlicePipe,
    NgTemplateOutlet,
    AddIconComponent,
    ArrowLeftIconComponent,
    ChevronDownIconComponent,
    CloseIconComponent,
    InfoIconComponent,
    RefreshIconComponent,
    BadgeComponent,
    StatusPipe,
    TranslocoPipe,
    DatePipe,
    DropdownOverlayDirective,
  ],
  templateUrl: './lookup-field.component.html',
  styleUrl: './lookup-field.component.scss',
  providers: [
    LookupResourceLinkService,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LookupFieldComponent),
      multi: true,
    },
  ],
  viewProviders: [
    {
      provide: ControlContainer,
      useFactory: () => inject(ControlContainer, { skipSelf: true }),
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class LookupFieldComponent implements OnInit, OnDestroy {
  subscribe = new Subject();
  lookupFieldService = inject(LookupFieldService);
  formBuilderService = inject(FormBuilderService);
  private readonly featureFlagService = inject(FeatureFlagsService);
  private readonly triggerActionHandler = inject(TriggerActionHandler, {
    optional: true,
  });
  private readonly router = inject(Router);
  private readonly tenantService = inject(TenantService);
  private readonly lookupResourceLink = inject(LookupResourceLinkService);

  /** Single open state — two-way bound to `dropdownOverlay`. */
  readonly lookupDropdownOpen = signal(false);

  constructor() {
    // Load options while open; effect cleanup resets state on close (not on init).
    effect(onCleanup => {
      if (!this.lookupDropdownOpen()) {
        return;
      }

      untracked(() => this.loadLookupDropdownData());
      onCleanup(() => {
        this.clickedOutside();
      });
    });
  }

  inputFormControlName = input.required<string | number>();
  id = input.required<string>();
  name = input.required<string>();
  label = input<string>('Label');
  labelClass = input<string>('');
  placeholder = input<string>('Placeholder');
  searchPlaceholder = input<string>('Type to search');
  inputClass = input<string>('');
  disabled = input<boolean>(false);
  viewOnly = input<boolean>(false);
  isMultiLookup = input<boolean>(false); // used in multi lookup field
  selectedOptionsArray = input<number[] | null>([]); // used in multi lookup field
  selectAllLabel = input<string>('Select All');
  enableSelectAll = input<boolean>(false);
  maxSelectable = input<number | null>(null);
  selectAllOptions = output<LookupOptionItem[]>();
  readonly isSelectingAll = signal(false);
  readonly = input<boolean>();
  value = input<string | number>();
  required = input<boolean>(false);
  labelBadge = input<TemplateRef<unknown>>();
  options = model<LookupOptionItem[]>([]);
  selectedKeyValue = input<'value' | 'id'>('value');
  selectedKeyName = input<'label' | 'name' | 'bank_name'>('label');
  noSearchResultLabel = input<string>('No Data');
  detailsNotFoundLabel = input<string>('N/A');
  triggerAction = input<TriggerAction | null>(null);
  parentFieldKey = input<string | null>(null);
  selectedOption = model<LookupOptionItem | undefined>(undefined);
  relationDetails = input<LookupOptionItem | undefined>(undefined);
  relationResource = input<LookupResource | null>(null);
  selectedOptionOutput = output<LookupOptionItem>();
  viewDetails = output<LookupOptionItem>();
  clearOption = output<void>();
  allowCreate = input<boolean>(false);
  createLabel = input<string>('');
  createNew = output<string>();

  /**
   * Always opens below the trigger (no flip-to-top fallback), matching
   * `PaginatedSelectComponent`'s always-below panel.
   */
  readonly dropdownListPositions: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
      offsetY: 8,
    },
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
      offsetY: 8,
    },
  ];

  isDetailsTooltipOpen = signal<boolean>(false);
  readonly detailsTooltipShiftPx = signal(0);
  readonly detailsTooltipArrowLeftPx = signal<number | null>(null);
  /** Above the info icon, centered on it; falls back below when there is no room. */
  readonly detailsTooltipPositions: ConnectedPosition[] = [
    {
      originX: 'center',
      originY: 'top',
      overlayX: 'center',
      overlayY: 'bottom',
      offsetY: -10,
    },
    {
      originX: 'center',
      originY: 'bottom',
      overlayX: 'center',
      overlayY: 'top',
      offsetY: 10,
    },
  ];
  readonly parentAndChildApproachFlag = signal<boolean>(
    this.featureFlagService.getFeatureFlagValue(
      FEATURE_FLAGS.WORKFLOWS.parent_and_child_approach
    )
  ).asReadonly();
  /** Wider request-card dropdown when parent/child approach + Start action are active. */
  readonly isParentChildTriggerLookup = computed(
    () => this.parentAndChildApproachFlag() && !!this.triggerAction()
  );

  /**
   * Absolute URL of the looked-up resource details page, or `null` when the
   * lookup is not linkable. Drives the anchor rendered around the label.
   */
  readonly resourceUrl = computed<string | null>(() => {
    // Touch the signal so sibling control changes recompute the URL.
    this.lookupResourceLink.extraParamState();
    const displayOption = this.selectedOption() ?? this.relationDetails();

    return this.lookupResourceLink.buildResourceUrl(
      this.relationResource(),
      displayOption?.value,
      this.inputFormControlName(),
      this.parentFormGroup,
      displayOption
    );
  });
  showNoSearchResult = signal(false);
  filteredOptions: LookupOptionItem[] = [];
  apiUrl = input<string>('');
  page = signal<number>(1);
  pageSize = signal<number>(15);
  totalPages = model<number>(1);
  totalItems = 0;
  isLoading = signal<boolean>(false);
  labelValueAssignList = model.required<AssignList>();
  filters = input<LookupFilter[]>([]);
  isSearchable = signal<boolean>(false);
  searchKeyword = signal<string>('');
  searchKeywordKey = input<string>('search');
  apiUrlParams: string[] = [];
  additionalKeys: string[] = [];
  destroy$ = new Subject();
  private readonly searchSource$ = new Subject<string>();

  /**
   * Configuration for handling additional data fetching when an option is selected.
   * Can be either a single OnSelectData object or an array of OnSelectData objects.
   *
   * Single object example:
   * { api: '/api/user-details/:userId', filters: ['department'] }
   *
   * Array example:
   * [
   *   { api: '/api/user-details/:userId', filters: ['department'] },
   *   { api: '/api/permissions/:userId', filters: ['permissions'] }
   * ]
   */
  onSelectData = input<OnSelectData | OnSelectData[]>();
  onSelectDependencyField = signal<Record<string, unknown>[]>([]);
  equation = input<string | undefined>(undefined);
  subscription: Subscription = new Subscription();

  private readonly parentContainer = inject(ControlContainer);

  /**
   * Immediate control from ControlContainer.
   * Inside multi-lookup this is a FormArray (via formArrayName); otherwise a FormGroup.
   */
  private get containerControl(): FormGroup | FormArray {
    return this.parentContainer.control as FormGroup | FormArray;
  }

  /**
   * Root form group used for named field lookups (dynamic filters, equations, on-select).
   * When nested under multi-lookup's formArrayName, ControlContainer is the FormArray,
   * so project_id and other sibling fields must be resolved from the root FormGroup.
   */
  private get parentFormGroup(): FormGroup {
    const control = this.containerControl;
    if (control instanceof FormArray) {
      return control.root as FormGroup;
    }
    return control as FormGroup;
  }

  protected get childControl(): FormControl {
    const container = this.containerControl;
    if (container instanceof FormArray) {
      return container.at(Number(this.inputFormControlName())) as FormControl;
    }
    return container.controls[this.inputFormControlName()] as FormControl;
  }

  ngOnInit(): void {
    if (this.disabled() || this.viewOnly()) {
      this.childControl.disable();
    }
    // relation_details / selectedOption can be present while field.value (and the
    // FormControl) is still empty. Sync immediately so sibling filters that depend
    // on this control (e.g. paginated-select) see a value during their ngOnInit.
    this.syncControlFromSelectedOption();
    // If an external value was assigned to the FormControl before this component rendered,
    // ensure we load the option and sync the selected option state now.
    const initialAssignedValue = (this.childControl?.value ?? this.value()) as
      | number
      | undefined;
    if (
      initialAssignedValue !== undefined &&
      initialAssignedValue !== null &&
      !this.selectedOption() &&
      this.apiUrl()
    ) {
      this.getLookupItems(initialAssignedValue);
    }

    // check if is the search function is in filters
    const search = this.filters().filter(
      f => f.key === this.searchKeywordKey()
    ).length;
    if (search > 0 && this.filters().length) {
      this.isSearchable.set(true);
      this.resetSearchFilter();
      this.lookupSearch();
    } else {
      this.handleLookupFilters();
    }

    if (this.filters()?.length) {
      this.filters().forEach((filter, index) => {
        if (filter.value_type === filterInputTypesEnum.DYNAMIC) {
          const fieldControlName = filter.value.toString();
          this.handleControlDependencies(fieldControlName, index, filter);
        }
      });
    }

    this.subscription.add(
      of(null)
        .pipe(delay(1))
        .subscribe(() => {
          this.handleSelectedOption(
            this.relationDetails() || this.selectedOption()
          );
        })
    );
    this.handleEquation();
    this.lookupResourceLink.watchResourceExtraParams(
      this.relationResource(),
      this.inputFormControlName(),
      this.parentFormGroup,
      this.selectedOption() ?? this.relationDetails()
    );

    // Watch for programmatic value assignments to the child control (e.g., from another lookup)
    // and ensure the selected option is synchronized even if options are not yet loaded.
    this.childControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        // Ignore string values (these should only come from search keyword, not form control)
        if (typeof value === 'string') {
          return;
        }
        if (
          value !== null &&
          value !== undefined &&
          (!this.selectedOption() || this.selectedOption()?.value !== value)
        ) {
          // If options already exist, just set selected; otherwise fetch
          const numericValue = value as number;
          if (this.options().length) {
            const option = this.getSelectedOption(numericValue);
            if (option) {
              this.selectedOption.set(option);
            } else {
              this.getLookupItems(numericValue);
            }
          } else {
            this.getLookupItems(numericValue);
          }
        }
      });
  }

  /**
   * Normalizes onSelectData to always return an array format
   * Handles both single object and array inputs
   */
  private getOnSelectDataArray(): OnSelectData[] {
    const data = this.onSelectData();
    if (!data) return [];
    return Array.isArray(data) ? data : [data];
  }

  handleSelectedOption(selectedOption: LookupOptionItem | undefined) {
    if (selectedOption) {
      this.addAdditionalKeysFormControl(selectedOption);
      this.setAdditionalKeysFormControl(selectedOption as unknown);
      const shouldUpdateControl = this.isEmptyControlValue(
        this.childControl?.value
      );
      this.selectOption(selectedOption, shouldUpdateControl);
    }
  }

  private getSelectedOptionValue(
    option: LookupOptionItem | undefined
  ): string | number | undefined {
    if (!option) {
      return undefined;
    }
    const keyedValue = option[this.selectedKeyValue()];
    if (!this.isEmptyControlValue(keyedValue)) {
      return keyedValue as string | number;
    }
    if (!this.isEmptyControlValue(option.value)) {
      return option.value;
    }
    return option.id;
  }

  private isEmptyControlValue(value: unknown): boolean {
    return (
      value === null ||
      value === undefined ||
      (typeof value === 'string' && !value.trim())
    );
  }

  private syncControlFromSelectedOption(): void {
    const option = this.selectedOption() ?? this.relationDetails();
    const selectedValue = this.getSelectedOptionValue(option);
    if (
      this.isEmptyControlValue(selectedValue) ||
      !this.isEmptyControlValue(this.childControl?.value)
    ) {
      return;
    }
    this.childControl.patchValue(selectedValue);
  }

  handleControlDependencies(
    fieldControlName: string,
    index: number,
    filter: LookupFilter
  ) {
    const fieldControl = this.parentFormGroup?.get(fieldControlName);
    if (fieldControl?.value) {
      this.apiUrlParams[index] = fieldControl.value;
    }

    fieldControl?.valueChanges
      ?.pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (value === null || value === undefined || !value.toString().trim()) {
          this.childControl.disable();
        } else if (this.isDependencyFieldsValueAvailable()) {
          this.childControl.enable();
        }
        this.selectedOption.set(undefined);
        this.childControl.patchValue(null);
        this.apiUrlParams[index] = value;

        if (
          this.getOnSelectDataArray().length &&
          this.onSelectDependencyField().length
        ) {
          this.setOnSelectDependencyField(true);
        }
      });

    // check if the field control value is null, undefined, or empty string
    if (
      (fieldControl?.value === null ||
        fieldControl?.value === undefined ||
        !fieldControl?.value?.toString().trim()) &&
      filter.required
    ) {
      this.childControl.disable();
    }
  }

  isDependencyFieldsValueAvailable(): boolean {
    return this.filters().every(filter => {
      if (
        filter.value_type === filterInputTypesEnum.DYNAMIC &&
        filter.required
      ) {
        const fieldControlName = filter.value.toString();
        const fieldControl =
          this.parentFormGroup?.get(fieldControlName)?.value ??
          this.parentFormGroup?.controls[fieldControlName]?.value;
        return !!fieldControl;
      }
      return true;
    });
  }

  handleEquation() {
    if (this.equation()) {
      const dynamicEquationParams = [
        this.equation() as string,
        this.inputFormControlName() as string,
        this.parentFormGroup,
      ];

      if (!this.equation()?.includes(' ')) {
        this.handleSingleEquation(dynamicEquationParams);
      }
    }
  }

  handleSingleEquation(dynamicEquationParams: (string | FormGroup)[]) {
    const equationControl = this.formBuilderService.handleLookupEquationKey(
      (dynamicEquationParams[0] as string).substring(1),
      dynamicEquationParams[1] as string,
      dynamicEquationParams[2] as FormGroup
    );

    // to handle lookup equation value
    if (equationControl) {
      // Set initial value
      this.setEquationSelectedOption(
        equationControl.value ? JSON.stringify(equationControl.value) : ''
      );

      // Subscribe to changes
      const subscription = equationControl.valueChanges.subscribe(value => {
        this.setEquationSelectedOption(value ? JSON.stringify(value) : '');
      });

      this.subscription.add(subscription);
    }
  }

  setEquationSelectedOption(value: string) {
    if (!value) {
      this.selectedOption.set(undefined);
      this.childControl.setValue(null);
      return;
    }

    const lookupValue: LookupOptionItem = {
      ...JSON.parse(value),
      value: JSON.parse(value)?.value ?? null,
      label: JSON.parse(value)?.label ?? null,
    };
    this.selectedOption.set(lookupValue);
    this.childControl.setValue(lookupValue.value);
  }

  resetSearchFilter() {
    const searchFilterIndex = this.filters().findIndex(
      f => f.key === this.searchKeywordKey()
    );
    this.filters()[searchFilterIndex].value = '';
    this.searchKeyword.set('');
  }

  ngOnDestroy(): void {
    this.closeDetailsTooltip();
    this.subscribe.next(1);
    this.subscribe.complete();

    this.destroy$.next(1);
    this.destroy$.complete();

    this.searchSource$.complete();

    this.subscription.unsubscribe();
  }

  /** Loads options when the CDK overlay opens. */
  private loadLookupDropdownData(): void {
    if (this.isLoading()) {
      return;
    }

    this.filteredOptions = [];
    this.options.set([]);
    this.childControl.markAsTouched();
    this.isLoading.set(true);

    const searchFilterIndex = this.filters().findIndex(
      f => f.key === this.searchKeywordKey()
    );
    if (
      !this.options().length &&
      (!this.filters().length ||
        (this.filters().length === 1 &&
          searchFilterIndex !== undefined &&
          searchFilterIndex !== null &&
          searchFilterIndex !== -1 &&
          this.filters()[searchFilterIndex].value &&
          this.filters()[searchFilterIndex].value.length <
            SearchMinLength.length))
    ) {
      this.getLookupItems();
    } else {
      this.getFilteredLookupItems();
    }
  }

  toggleDropdown() {
    // Kept for programmatic callers; overlay host click uses the directive toggle.
    this.lookupDropdownOpen.update(open => !open);
  }

  getLookupItems(value?: number) {
    if (!this.apiUrl()) {
      this.isLoading.set(false);
      return;
    }
    this.isLoading.set(true);
    this.lookupFieldService
      .getLookupOptions(this.apiUrl(), this.page())
      .subscribe({
        next: (response: ApiResponse<LookupOptionItem>) => {
          let filteredOptions = [];
          if (this.selectedOptionsArray()?.length) {
            filteredOptions = response['data'].filter(
              item =>
                !this.selectedOptionsArray()?.includes(item.value as number)
            );
          } else {
            filteredOptions = response['data'];
          }
          this.addAdditionalKeysFormControl(filteredOptions[0]);
          this.options().push(...filteredOptions);
          this.totalPages.set(response['meta']['last_page']);
          if ((this.value() || value) && !this.selectedOption()) {
            this.selectedOption.set(
              this.getSelectedOption(
                (this.value() as number) || (value as number)
              )
            );
          }
          this.isLoading.set(false);
        },
        error: () => {
          this.isLoading.set(false);
          this.showNoSearchResult.set(true);
        },
      });
  }

  onScroll(event: Event) {
    const { scrollTop, scrollHeight, clientHeight } =
      event.target as unknown as {
        scrollTop: number;
        scrollHeight: number;
        clientHeight: number;
      };
    if (
      scrollTop + clientHeight >= scrollHeight - 200 &&
      !this.isLoading() &&
      this.page() < this.totalPages()
    ) {
      this.page.set(this.page() + 1);
      const searchFilterIndex = this.filters().findIndex(
        f => f.key === this.searchKeywordKey()
      );

      if (
        !this.options().length &&
        (!this.filters().length ||
          (this.filters().length === 1 &&
            searchFilterIndex !== undefined &&
            searchFilterIndex !== null &&
            searchFilterIndex !== -1 &&
            this.filters()[searchFilterIndex].value &&
            this.filters()[searchFilterIndex].value.length <
              SearchMinLength.length))
      ) {
        this.getLookupItems();
      } else {
        this.getFilteredLookupItems();
      }
    }
  }

  getSelectedOption = (_value: number) =>
    this.options().find(({ value }) => value == _value);

  selectOption(item: LookupOptionItem, updateFormControlValue?: boolean) {
    if (item) {
      if (Array.isArray(item)) {
        const value = this.childControl.value;
        item = item.find(i => i[this.selectedKeyValue()] === value);
      }
      item.list = [];
      this.selectedOption.set(item);
      this.selectedOptionOutput.emit(item);
      if (updateFormControlValue) {
        // patch the additional keys to it's form control for show condition use
        this.setAdditionalKeysFormControl(item as unknown);

        this.childControl.patchValue(item[this.selectedKeyValue()]);
      }
      // Keep dynamic extra_params (e.g. :project_id) in sync with selection.
      this.lookupResourceLink.syncExtraParamsFromOption(
        this.relationResource(),
        item
      );
      this.filteredOptions = [];
      this.searchKeyword.set('');
      this.getLookupOptionsRelatedData();
    }

    this.selectedOption()?.list?.push(
      ...this.labelValueAssignList().extraLabels
    );
  }

  /** Visible dropdown rows, preferring the active search/filter list. */
  private dropdownOptions(): LookupOptionItem[] {
    return this.filteredOptions.length ? this.filteredOptions : this.options();
  }

  /** Remaining slots the parent multi-lookup can accept, or null when uncapped. */
  private remainingSelectableCount(): number | null {
    const maxSelectable = this.maxSelectable();
    if (maxSelectable == null) {
      return null;
    }

    const selectedCount = (this.selectedOptionsArray() ?? []).filter(
      value => value != null
    ).length;

    return Math.max(0, maxSelectable - selectedCount);
  }

  onCreateNewClick(): void {
    this.createNew.emit(this.searchKeyword());
  }

  showSelectAll(): boolean {
    return (
      this.enableSelectAll() &&
      this.isMultiLookup() &&
      !this.showNoSearchResult() &&
      this.dropdownOptions().length > 0 &&
      this.remainingSelectableCount() !== 0
    );
  }

  selectAllFromDropdown(): void {
    if (!this.enableSelectAll() || this.isSelectingAll() || this.isLoading()) {
      return;
    }

    const remaining = this.remainingSelectableCount();
    if (remaining === 0) {
      this.lookupDropdownOpen.set(false);
      return;
    }

    const displayed = this.dropdownOptions();
    const hasMorePages = this.page() < this.totalPages();
    const enoughLoaded = remaining != null && displayed.length >= remaining;

    if (!hasMorePages || enoughLoaded) {
      this.emitSelectAllAndClose(displayed, remaining);
      return;
    }

    this.fetchRemainingForSelectAll(remaining);
  }

  private fetchRemainingForSelectAll(limit: number | null): void {
    const loaded = this.dropdownOptions();
    const remainingPages = Array.from(
      { length: this.totalPages() - this.page() },
      (_, index) => this.page() + index + 1
    );

    this.isSelectingAll.set(true);
    this.isLoading.set(true);

    forkJoin(
      remainingPages.map(page =>
        this.lookupFieldService.getLookupOptions(
          this.apiUrl(),
          page,
          this.filters(),
          this.apiUrlParams
        )
      )
    )
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isSelectingAll.set(false);
          this.isLoading.set(false);
        })
      )
      .subscribe({
        next: (responses: ApiResponse<LookupOptionItem>[]) => {
          const fromPages = responses.flatMap(response => response.data ?? []);
          this.emitSelectAllAndClose([...loaded, ...fromPages], limit);
        },
        error: () => {
          this.emitSelectAllAndClose(loaded, limit);
        },
      });
  }

  private emitSelectAllAndClose(
    items: LookupOptionItem[],
    limit: number | null
  ): void {
    this.selectAllOptions.emit(this.uniqueUnselectedOptions(items, limit));
    this.lookupDropdownOpen.set(false);
  }

  private uniqueUnselectedOptions(
    items: LookupOptionItem[],
    limit: number | null
  ): LookupOptionItem[] {
    const selectedKeys = new Set(
      (this.selectedOptionsArray() ?? [])
        .filter(value => value != null)
        .map(String)
    );
    const seen = new Set<string>();
    const unique: LookupOptionItem[] = [];

    for (const item of items) {
      const key = String(item[this.selectedKeyValue()] ?? item.value);
      if (!key || selectedKeys.has(key) || seen.has(key)) {
        continue;
      }

      seen.add(key);
      unique.push(item);

      if (limit != null && unique.length >= limit) {
        break;
      }
    }

    return unique;
  }

  onDetailsTooltipOpenChange(
    isOpen: boolean,
    option: LookupOptionItem,
    anchorEl: HTMLElement
  ): void {
    if (!isOpen) {
      this.closeDetailsTooltip();
      return;
    }
    this.isDetailsTooltipOpen.set(true);
    this.viewDetails.emit(option);
    // The overlay is attached on a later change-detection pass; wait for it.
    this.keepDetailsTooltipInViewport(anchorEl, 0);
  }

  /**
   * Shifts the tooltip horizontally so it stays inside the viewport while the
   * arrow keeps pointing at the info icon.
   */
  private keepDetailsTooltipInViewport(
    anchorEl: HTMLElement,
    attempt: number
  ): void {
    if (!this.isDetailsTooltipOpen()) {
      return;
    }
    const panel = document.getElementById(
      `lookup-details-tooltip-${this.id()}`
    );
    if (!panel || panel.getBoundingClientRect().width === 0) {
      if (attempt < 20) {
        requestAnimationFrame(() =>
          this.keepDetailsTooltipInViewport(anchorEl, attempt + 1)
        );
      }
      return;
    }
    const safePx = 8;
    // clientWidth excludes the page scrollbar, unlike innerWidth
    const viewportWidth = document.documentElement.clientWidth;
    const arrowEdgePx = 16;
    const anchorRect = anchorEl.getBoundingClientRect();
    // Measure from the unshifted position
    const panelRect = panel.getBoundingClientRect();
    const baseLeft = panelRect.left - this.detailsTooltipShiftPx();
    const baseRight = baseLeft + panelRect.width;
    let shift = 0;
    if (baseLeft < safePx) {
      shift = safePx - baseLeft;
    } else if (baseRight > viewportWidth - safePx) {
      shift = viewportWidth - safePx - baseRight;
    }
    const iconCenterX = anchorRect.left + anchorRect.width / 2;
    const arrowLeft = iconCenterX - (baseLeft + shift);
    this.detailsTooltipShiftPx.set(shift);
    this.detailsTooltipArrowLeftPx.set(
      Math.min(panelRect.width - arrowEdgePx, Math.max(arrowEdgePx, arrowLeft))
    );
  }

  closeDetailsTooltip(): void {
    this.isDetailsTooltipOpen.set(false);
    this.detailsTooltipShiftPx.set(0);
    this.detailsTooltipArrowLeftPx.set(null);
  }

  clearSelectedOption() {
    this.resetAdditionalKeysFormControlValue();
    this.selectedOption.set(undefined);
    this.lookupResourceLink.clearOptionDerivedExtraParams(
      this.relationResource()
    );
    this.childControl.reset();
    this.searchKeyword.set('');
    this.page.set(1);
    this.isLoading.set(false);
    this.closeDetailsTooltip();
    this.clearOption.emit();

    const searchFilterIndex = this.filters().findIndex(
      f => f.key === this.searchKeywordKey()
    );
    if (searchFilterIndex !== -1) {
      this.filters()[searchFilterIndex].value = '';
    }

    // Only re-query via search pipeline when the dropdown is open.
    // Emitting while closed previously left isLoading stuck true (no emission).
    if (this.lookupDropdownOpen()) {
      this.searchSource$.next('');
    }

    if (this.onSelectDependencyField().length) {
      this.setOnSelectDependencyField(true);
    }
  }

  onDropdownSearch(event: Event) {
    if (event.target) {
      const searchValue = (<HTMLInputElement>event.target).value;
      if (this.options()) {
        if (searchValue) {
          this.filteredOptions = this.options().filter(
            (item: LookupOptionItem) => {
              return item?.label
                ? item?.label
                    .toLocaleLowerCase()
                    .includes(searchValue.toLocaleLowerCase())
                : '';
            }
          );
          if (!this.filteredOptions.length) {
            this.showNoSearchResult.set(true);
          } else {
            this.showNoSearchResult.set(false);
          }
        } else {
          this.filteredOptions = [];
          this.showNoSearchResult.set(false);
        }
      } else {
        this.showNoSearchResult.set(true);
      }
    }
  }

  getFilteredLookupItems() {
    this.isLoading.set(true);
    this.lookupFieldService
      .getLookupOptions(
        this.apiUrl(),
        this.page(),
        this.filters(),
        this.apiUrlParams
      )
      .subscribe({
        next: (res: ApiResponse<LookupOptionItem>) => {
          let filteredOptions = [];
          if (this.selectedOptionsArray()?.length) {
            filteredOptions = res['data'].filter(
              item =>
                !this.selectedOptionsArray()?.includes(item.value as number)
            );
          } else {
            filteredOptions = res['data'];
          }
          this.addAdditionalKeysFormControl(filteredOptions[0]);
          this.filteredOptions.push(...filteredOptions);
          if (res.meta) {
            this.totalPages.set(res['meta']['last_page']);
          }

          if (!this.filteredOptions.length) {
            this.showNoSearchResult.set(true);
          } else {
            this.showNoSearchResult.set(false);
          }
          this.isLoading.set(false);
        },
        error: (_error: unknown) => {
          this.isLoading.set(false);
          this.showNoSearchResult.set(true);
        },
      });
  }

  /** Resets list/search state when the overlay closes. */
  clickedOutside() {
    this.page.set(1);
    this.filteredOptions = [];
    this.options.set([]);
    this.isLoading.set(false);
    if (
      this.childControl?.touched &&
      !this.childControl?.value &&
      this.childControl?.hasError('required')
    ) {
      this.childControl.markAsDirty();
    }

    const searchFilterIndex = this.filters().findIndex(
      f => f.key === this.searchKeywordKey()
    );
    if (!this.selectedOption() && this.filters()[searchFilterIndex]?.value) {
      this.childControl.reset();
      this.searchKeyword.set('');
      this.filters()[searchFilterIndex].value = '';
    }
  }

  lookupSearch() {
    // Subscribe to search keyword changes via custom observable
    this.searchSource$
      .pipe(
        debounceTime(500),
        switchMap((value: string) => {
          const searchFilterIndex = this.filters().findIndex(
            f => f.key === this.searchKeywordKey()
          );
          if (searchFilterIndex !== -1) {
            this.filters()[searchFilterIndex].value = value;
          }
          if (!this.selectedOption() && this.lookupDropdownOpen()) {
            if (
              searchFilterIndex !== -1 &&
              !(value && value.length >= SearchMinLength.length)
            ) {
              this.filters()[searchFilterIndex].value = '';
            }
            this.page.set(1);
            this.options.set([]);
            this.filteredOptions = [];
            this.isLoading.set(true);
            return this.lookupFieldService
              .getLookupOptions(
                this.apiUrl(),
                this.page(),
                this.filters(),
                this.apiUrlParams
              )
              .pipe(finalize(() => this.isLoading.set(false)));
          }
          // Returning [] previously completed without next, so the old
          // tap(isLoading=false) never ran and stuck the spinner forever.
          return EMPTY;
        }),
        takeUntil(this.subscribe.asObservable())
      )
      .subscribe({
        next: (res: ApiResponse<LookupOptionItem>) => {
          let filteredOptions = [];
          if (this.selectedOptionsArray()?.length) {
            filteredOptions = res['data'].filter(
              item =>
                !this.selectedOptionsArray()?.includes(item.value as number)
            );
          } else {
            filteredOptions = res['data'];
          }
          this.addAdditionalKeysFormControl(filteredOptions[0]);
          this.filteredOptions = filteredOptions;
          this.totalPages.set(res['meta']['last_page']);

          if (!this.filteredOptions.length) {
            this.showNoSearchResult.set(true);
          } else {
            this.showNoSearchResult.set(false);
          }
        },
      });
  }

  onSearchInputChange(value: string) {
    this.searchKeyword.set(value);
    this.searchSource$.next(value);
  }

  handleLookupFilters() {
    this.filters().forEach(f => {
      f.value = f.value.replace('{', '').replace('}', '');
    });
  }

  addAdditionalKeysFormControl(data: LookupOptionItem) {
    if (data) {
      this.additionalKeys = Object.keys(data).filter(
        key => !['id', 'value', 'label'].includes(key)
      );
    }
  }

  setAdditionalKeysFormControl(item: unknown) {
    if (!this.additionalKeys.length) {
      return;
    }
    const record = item as Record<string, unknown>;
    this.additionalKeys.forEach(key => {
      const childControlName = `${this.inputFormControlName()}.${key}`;
      const control =
        this.parentFormGroup instanceof FormGroup
          ? this.parentFormGroup.controls[childControlName]
          : undefined;
      if (control) {
        control.patchValue(this.setAdditionalKeysFormControlValue(record, key));
      }
    });
  }

  setAdditionalKeysFormControlValue(
    item: Record<string, unknown>,
    key: string
  ) {
    const value = item[key];
    return typeof value === 'object' ? JSON.stringify(value) : value;
  }

  resetAdditionalKeysFormControlValue() {
    if (this.additionalKeys.length) {
      this.additionalKeys.forEach(key => {
        const childControlName = `${this.inputFormControlName()}.${key}`;
        const childControl = this.parentFormGroup.controls[childControlName];
        if (childControl) {
          childControl.reset();
        }
      });
    }
  }

  getLookupOptionsRelatedData() {
    const onSelectDataArray = this.getOnSelectDataArray();
    if (onSelectDataArray.length) {
      onSelectDataArray.forEach((onSelectItem, index) => {
        if (onSelectItem.api) {
          this.lookupFieldService
            .getLookupOptionsRelatedData(
              this.setOnSelectApi(onSelectItem),
              onSelectItem.filters,
              this.apiUrlParams
            )
            .subscribe({
              next: (res: unknown) => {
                const typed = res as { data: Record<string, unknown> };
                this.formBuilderService.setReplacementKey(
                  onSelectItem.replacement_keys,
                  typed.data
                );
                this.onSelectDependencyField.update(current => {
                  const newData = [...current];
                  if (onSelectItem.data_key) {
                    typed.data = { [onSelectItem.data_key]: typed.data };
                  }
                  newData[index] = typed.data;
                  return newData;
                });
                this.setOnSelectDependencyField();
              },
            });
        }
      });
    }
  }

  setOnSelectDependencyField(removeData?: boolean) {
    const dependencyFields = this.onSelectDependencyField();
    const onSelectDataArray = this.getOnSelectDataArray();

    if (dependencyFields.length) {
      dependencyFields.forEach((dependencyField, index) => {
        if (dependencyField) {
          if (
            onSelectDataArray[index].data_key &&
            onSelectDataArray[index].data_type === 'array'
          ) {
            this.handleArrayDependencyField(dependencyField, index, removeData);
          } else {
            const flattenedKeys =
              this.formBuilderService.flattenKeys(dependencyField);
            this.setFlattenedKeys(flattenedKeys, removeData, index);
            // Also handle direct key mappings (non-flattened)
            this.handleDirectKeyMappings(removeData, index);
          }
        }
      });
    }
  }

  private handleArrayDependencyField(
    dependencyField: any,
    index: number,
    removeData?: boolean
  ): void {
    const onSelectDataArray = this.getOnSelectDataArray();
    const onSelectItem = onSelectDataArray[index];

    if (!onSelectItem.data_key) {
      return;
    }

    const dependencyFieldArray = dependencyField[
      onSelectItem.data_key
    ] as any[];

    if (dependencyFieldArray && dependencyFieldArray.length > 0) {
      const processedFlattenedKeysObj = this.processFlattenedKeysForArray(
        dependencyFieldArray,
        onSelectItem
      );
      const formArrayControlName = this.getFormArrayControlName(
        onSelectItem.data_key,
        onSelectItem
      );

      this.handleFormArrayControl(
        formArrayControlName,
        processedFlattenedKeysObj,
        dependencyFieldArray,
        onSelectItem,
        removeData
      );
    }
  }

  private processFlattenedKeysForArray(
    dependencyFieldArray: any[],
    onSelectItem: OnSelectData
  ): { [index: number]: string[] } {
    const flattenedKeysObj =
      this.formBuilderService.flattenKeysArray(dependencyFieldArray);

    const processedFlattenedKeysObj: { [index: number]: string[] } = {};
    Object.keys(flattenedKeysObj).forEach(arrayIndexStr => {
      const arrayIndex = parseInt(arrayIndexStr, 10);
      const originalKeys = flattenedKeysObj[arrayIndex];

      processedFlattenedKeysObj[arrayIndex] = originalKeys.map(key =>
        this.formBuilderService.setControlNameDependency(key, onSelectItem)
      );
    });

    return processedFlattenedKeysObj;
  }

  private getFormArrayControlName(
    dataKey: string | undefined,
    onSelectItem: OnSelectData
  ): string {
    if (!dataKey) {
      return '';
    }
    return `${this.inputFormControlName()}.${this.formBuilderService.setControlNameDependency(
      dataKey,
      onSelectItem
    )}`;
  }

  private handleFormArrayControl(
    formArrayControlName: string,
    processedFlattenedKeysObj: { [index: number]: string[] },
    dependencyFieldArray: any[],
    onSelectItem: OnSelectData,
    removeData?: boolean
  ): void {
    const formArrayControl =
      this.parentFormGroup.controls[formArrayControlName];

    if (formArrayControl && formArrayControl instanceof FormArray) {
      this.updateExistingFormArray(
        formArrayControl as FormArray<any>,
        processedFlattenedKeysObj,
        dependencyFieldArray,
        onSelectItem,
        removeData
      );
    } else {
      this.createNewFormArray(
        formArrayControlName,
        processedFlattenedKeysObj,
        dependencyFieldArray,
        onSelectItem,
        removeData
      );
    }
  }

  private updateExistingFormArray(
    formArrayControl: FormArray<any>,
    processedFlattenedKeysObj: { [index: number]: string[] },
    dependencyFieldArray: any[],
    onSelectItem: OnSelectData,
    removeData?: boolean
  ): void {
    const flattenedKeysObj =
      this.formBuilderService.flattenKeysArray(dependencyFieldArray);

    // Update existing FormArray with processed flattened keys
    this.formBuilderService.setFormArrayWithFlattenedKeys(
      formArrayControl,
      processedFlattenedKeysObj,
      flattenedKeysObj,
      dependencyFieldArray,
      removeData
    );

    // Also handle direct key mappings for form arrays
    this.formBuilderService.handleFormArrayDirectKeyMappings(
      formArrayControl,
      dependencyFieldArray,
      onSelectItem,
      removeData
    );
  }

  private createNewFormArray(
    formArrayControlName: string,
    processedFlattenedKeysObj: { [index: number]: string[] },
    dependencyFieldArray: any[],
    onSelectItem: OnSelectData,
    removeData?: boolean
  ): void {
    const flattenedKeysObj =
      this.formBuilderService.flattenKeysArray(dependencyFieldArray);

    // Create new FormArray if it doesn't exist
    const newFormArray =
      this.formBuilderService.createFormArrayFromFlattenedKeys(
        processedFlattenedKeysObj,
        flattenedKeysObj,
        dependencyFieldArray
      );

    // Add the FormArray to the parent form group
    this.parentFormGroup.addControl(formArrayControlName, newFormArray);

    // Handle direct key mappings for the newly created FormArray
    this.formBuilderService.handleFormArrayDirectKeyMappings(
      newFormArray,
      dependencyFieldArray,
      onSelectItem,
      removeData
    );
  }

  setFlattenedKeys(
    flattenedKeys: string[],
    removeData?: boolean,
    dataIndex?: number
  ) {
    flattenedKeys.forEach(key => {
      const onSelectDataArray = this.getOnSelectDataArray();
      const onSelectItem =
        dataIndex !== undefined
          ? onSelectDataArray[dataIndex]
          : onSelectDataArray[0];

      const controlName = this.formBuilderService.setControlNameDependency(
        key,
        onSelectItem
      );

      const fullControlName = `${this.inputFormControlName()}.${controlName}`;
      const control = this.parentFormGroup.controls[fullControlName];

      const dependencyFields = this.onSelectDependencyField();
      const dependencyField =
        dataIndex !== undefined
          ? dependencyFields[dataIndex]
          : dependencyFields[0];

      const value = dependencyField
        ? this.formBuilderService.getValueByFlattenedKey(dependencyField, key)
        : null;

      if (!control) return;

      if (control instanceof FormArray) {
        this.updateFormArrayControl(control, value, removeData);
      } else {
        this.updateRegularControl(control, value, removeData);
      }
    });
  }

  private updateRegularControl(
    control: AbstractControl,
    value: unknown,
    removeData?: boolean
  ): void {
    control.setValue(removeData ? null : value);
  }

  private updateFormArrayControl(
    formArray: FormArray,
    value: unknown,
    removeData?: boolean
  ): void {
    formArray.clear();

    if (Array.isArray(value) && !removeData) {
      value.forEach((item: unknown) => {
        formArray.push(new FormControl(item));
      });
    }
  }

  handleDirectKeyMappings(removeData?: boolean, dataIndex?: number) {
    const dependencyFields = this.onSelectDependencyField();
    const dependencyField =
      dataIndex !== undefined
        ? dependencyFields[dataIndex]
        : dependencyFields[0];
    if (dependencyField) {
      Object.keys(dependencyField).forEach(key => {
        const onSelectDataArray = this.getOnSelectDataArray();
        const onSelectItem =
          dataIndex !== undefined
            ? onSelectDataArray[dataIndex]
            : onSelectDataArray[0];

        const controlName = this.formBuilderService.setControlNameDependency(
          `${this.inputFormControlName()}.${key}`,
          onSelectItem
        );

        const controlValue = !removeData ? dependencyField[key] : null;
        if (
          onSelectItem.data_type === 'array' &&
          onSelectItem.data_key &&
          dependencyField[onSelectItem.data_key]
        ) {
          // Handle array data type case
          const arrayValue = dependencyField[onSelectItem.data_key];
          this.parentFormGroup.controls[controlName]?.setValue(arrayValue);
        }

        this.subscription.add(
          of(null)
            .pipe(delay(1))
            .subscribe(() => {
              if (this.parentFormGroup.controls[controlName]) {
                this.parentFormGroup.controls[controlName].setValue(
                  controlValue
                );
              }
            })
        );
      });
    }
  }

  setOnSelectApi(onSelectItem: OnSelectData): string {
    let apiUrl = onSelectItem.api;
    if (apiUrl.includes(':')) {
      const apiUrlSegments = apiUrl.split('?')[0].split('/');
      const apiUrlParamKeys = apiUrlSegments.filter(segment =>
        segment.startsWith(':')
      );
      const apiUrlParamValues = apiUrlParamKeys.map(key => {
        const keyWithoutColon = key.substring(1);
        return this.parentFormGroup.controls[keyWithoutColon].value;
      });

      apiUrlParamKeys.forEach((key, index) => {
        apiUrl = apiUrl.replace(key, apiUrlParamValues[index]);
      });
    }
    return apiUrl;
  }

  onTriggerActionClick(triggerAction: TriggerAction, event: Event): void {
    event.stopPropagation();
    this.lookupDropdownOpen.set(false);
    this.triggerActionHandler?.handle(triggerAction, this.parentFieldKey());
  }

  /**
   * Parent/child create_action lookups store flow ids; open request details
   * when a selected item is clicked (create + view).
   */
  navigateToSelectedRequest(
    option: LookupOptionItem | undefined,
    event?: Event
  ): void {
    event?.stopPropagation();
    // relation_resource is the newer, backend-driven mechanism and takes over
    // the whole selected box when it resolves a link.
    if (
      this.resourceUrl() ||
      !this.isParentChildTriggerLookup() ||
      option?.value == null
    ) {
      return;
    }

    this.router.navigate([
      this.tenantService.tenantPath(
        `${EMPLOYEE_ROUTES.REQUESTS.REQUEST_VIEW}/${option.value}`
      ),
    ]);
  }
}
